import { IconChevronRight } from "@tabler/icons-react";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "@/lib/utils";

type DietCardRowProps = ComponentProps<"button"> & {
  icon: ReactNode;
  label: ReactNode;
  /** Rendered right after the label, e.g. the rating badge. */
  addon?: ReactNode;
  labelClassName?: string;
  /** Overrides the default icon color, e.g. to match a colored label. */
  iconClassName?: string;
  /** Renders plain, non-clickable content when the row has nothing to open. */
  interactive?: boolean;
};

const ROW_CLASSES =
  "flex w-full items-center gap-2 border-brand-grey2-300 border-t px-4 py-4 text-left";

/**
 * One row of the diet card: icon, label and a chevron. Spreads the remaining
 * props onto the button so it can be handed to a dialog as its `trigger`.
 */
export const DietCardRow = ({
  icon,
  label,
  addon,
  className,
  labelClassName,
  iconClassName,
  interactive = true,
  ...props
}: DietCardRowProps) => {
  const content = (
    <>
      <span className={cn("shrink-0 text-brand-green1-300", iconClassName)}>
        {icon}
      </span>
      <span
        className={cn("min-w-0 truncate font-medium text-base", labelClassName)}
      >
        {label}
      </span>
      {addon}
      {interactive && (
        <IconChevronRight
          className="ml-auto size-6 shrink-0 text-brand-green1-300"
          stroke={1.5}
        />
      )}
    </>
  );

  if (!interactive) {
    return <div className={cn(ROW_CLASSES, className)}>{content}</div>;
  }

  return (
    <button
      className={cn(
        ROW_CLASSES,
        "py-3 transition-colors hover:bg-brand-grey1-100 md:h-14",
        className,
      )}
      type="button"
      {...props}
    >
      {content}
    </button>
  );
};
